import React, {useState} from 'react';
import {FlatList, StyleSheet, View} from 'react-native';

import StoreItem from '../../components/StoreItem';
import ActivityIndicator from '../../components/ui-controls/ActivityIndicator';
import Button from '../../components/ui-controls/Button';
import Text from '../../components/ui-controls/Text';
import config from '../../config';
import {AppRoute} from '../../models/AppRoute';
import {GeocodedSuburb, Shop} from '../../models/middleware';
import {useStores} from '../../stores/use-stores';
import {navigateToGiftCardsShop} from '../../utils/giftCards';
import SuburbSelectionModal from '../storeSelection/SuburbSelectionModal';

const MAX_DISTANCE_KM = 200;

type Props = {
  navigation: any;
  isRootStoreSelectionFlow?: boolean;
};

const GiftCardPromotionExtraContent = ({
  navigation,
  isRootStoreSelectionFlow = false,
}: Props) => {
  const {shopSelectionStore} = useStores();

  const [isSuburbSelectionModalVisible, setIsSuburbSelectionModalVisible] =
    useState(false);
  const [isShopAvailabilityLoading, setIsShopAvailabilityLoading] =
    useState(false);
  const [shopList, setShopList] = useState<Shop[]>([]);
  const [shopListAvailabilityMessage, setShopListAvailabilityMessage] =
    useState('');

  const handleCheckStoreAvailability = () =>
    setIsSuburbSelectionModalVisible(true);

  const handleTermsAndConditionsPress = () => {
    const target = isRootStoreSelectionFlow
      ? AppRoute.ROOT_GIFT_CARDS_TERMS_AND_CONDITIONS
      : AppRoute.GIFT_CARDS_TERMS_AND_CONDITIONS;
    navigation.navigate(target);
  };

  const handleSelectSuburb = async (suburb: GeocodedSuburb) => {
    setIsSuburbSelectionModalVisible(false);
    setIsShopAvailabilityLoading(true);
    const shopList = await shopSelectionStore.querySuburbStores(
      suburb,
      MAX_DISTANCE_KM,
    );
    // in future we will have gift cards available in store, and can remove this filter
    const availableShops = shopList.filter(
      shop => shop.delivery_available || shop.pickup_available,
    );
    setShopList(availableShops);
    const message =
      shopList.length > 0
        ? 'Awesome, we got you covered!'
        : 'Sorry, there are no stores currently servicing this area.';
    setShopListAvailabilityMessage(message);
    setIsShopAvailabilityLoading(false);
  };

  const handleCancel = () => setIsSuburbSelectionModalVisible(false);

  const renderItem = ({item}: {item: Shop}) => (
    <StoreItem item={item} dayName="today" />
  );

  return (
    <>
      <Text style={styles.topSeparationLarge}>
        If you are unsure whether there&apos;s a {config.APP_NAME} store near
        the Gift Card recipient, please check the availability of stores below.
      </Text>
      <Button
        containerStyle={styles.topSeparationSmall}
        onPress={handleCheckStoreAvailability}
        title="Check Store Location"
        type="outline"
      />
      {!isShopAvailabilityLoading && !!shopListAvailabilityMessage && (
        <Text align="center" style={styles.topSeparationSmall}>
          {shopListAvailabilityMessage}
        </Text>
      )}
      {!!isShopAvailabilityLoading && (
        <View style={styles.topSeparationSmall}>
          <ActivityIndicator size="small" />
        </View>
      )}
      {!isShopAvailabilityLoading && shopList.length > 0 && (
        <FlatList
          data={shopList.slice(0, 3)}
          renderItem={renderItem}
          keyExtractor={item => item.store_number}
          ItemSeparatorComponent={() => <View style={{height: 16}} />}
          fadingEdgeLength={100}
          showsVerticalScrollIndicator={false}
        />
      )}
      <Button
        containerStyle={styles.topSeparationLarge}
        onPress={handleTermsAndConditionsPress}
        title="Terms & Conditions"
        titleStyle={styles.link}
        type="clear"
      />
      <Button
        containerStyle={[styles.topSeparationSmall, {marginBottom: 16}]}
        onPress={navigateToGiftCardsShop}
        title={'BUY A GIFT CARD'}
      />
      <SuburbSelectionModal
        isVisible={isSuburbSelectionModalVisible}
        onCancel={handleCancel}
        onSelectSuburb={handleSelectSuburb}
        title="FIND STORES"
      />
    </>
  );
};

const styles = StyleSheet.create({
  topSeparationLarge: {
    marginTop: 16,
  },
  topSeparationSmall: {
    marginTop: 8,
  },
  link: {
    color: '#0054A6',
  },
});

export default GiftCardPromotionExtraContent;
